import { useMemo } from 'react';
import { cn } from '../../utils/cn';
import type { InputProps } from './input.types';
import styles from './input.module.css';

const sizeClasses = {
    small: styles.inputSmall,
    medium: styles.inputMedium,
    large: styles.inputLarge,
};

export const useInputStyles = ({
    variant = 'default',
    size = 'large',
    iconLeftProps,
    iconRightProps,
    error,
    errorProps,
    helperTextProps,
    disabled = false,
    containerClassName,
    inputClassName,
    labelClassName,
    className,
}: Pick<
    InputProps,
    | 'variant'
    | 'size'
    | 'iconLeftProps'
    | 'iconRightProps'
    | 'error'
    | 'errorProps'
    | 'helperTextProps'
    | 'disabled'
    | 'containerClassName'
    | 'inputClassName'
    | 'labelClassName'
    | 'className'
>) => {
    const variantDefaultClassNames = useMemo(() => cn(styles.inputDefault, error && styles.inputDefaultError), [error]);

    const variantFilledClassNames = useMemo(() => cn(styles.inputFilled, error && styles.inputFilledError), [error]);

    const variantClasses = useMemo(
        () => ({
            default: variantDefaultClassNames,
            filled: variantFilledClassNames,
        }),
        [variantDefaultClassNames, variantFilledClassNames]
    );

    const containerClassNames = useMemo(() => cn(styles.container, containerClassName), [containerClassName]);

    const labelOutsideClassNames = useMemo(
        () => cn(styles.label, disabled && styles.labelDisabled, error && styles.labelError, labelClassName),
        [disabled, error, labelClassName]
    );

    const labelFloatingClassNames = useMemo(
        () =>
            cn(
                styles.label,
                styles.labelFloating,
                variant === 'filled' && styles.labelFloatingFilled,
                (iconLeftProps?.icon || iconLeftProps?.name) && styles.labelFloatingWithLeftIcon,
                disabled && styles.labelDisabled,
                error && styles.labelError,
                labelClassName
            ),
        [variant, iconLeftProps?.icon, iconLeftProps?.name, disabled, error, labelClassName]
    );

    const inputClassNames = useMemo(
        () =>
            cn(
                styles.input,
                sizeClasses[size],
                variantClasses[variant],
                (iconLeftProps?.icon || iconLeftProps?.name) && styles.inputWithLeftIcon,
                (iconRightProps?.icon || iconRightProps?.name) && styles.inputWithRightIcon,
                disabled && styles.inputDisabled,
                inputClassName,
                className
            ),
        [
            size,
            variant,
            variantClasses,
            iconLeftProps?.icon,
            iconLeftProps?.name,
            iconRightProps?.icon,
            iconRightProps?.name,
            disabled,
            inputClassName,
            className,
        ]
    );

    const iconLeftContainerClassNames = useMemo(
        () =>
            cn(
                styles.iconButton,
                styles.iconButtonLeft,
                iconLeftProps?.onClick && !disabled && !iconLeftProps?.disabled && styles.iconButtonInteractive,
                (disabled || iconLeftProps?.disabled) && styles.iconButtonDisabled,
                iconLeftProps?.containerClassName
            ),
        [iconLeftProps?.onClick, disabled, iconLeftProps?.disabled, iconLeftProps?.containerClassName]
    );

    const iconRightContainerClassNames = useMemo(
        () =>
            cn(
                styles.iconButton,
                styles.iconButtonRight,
                iconRightProps?.onClick && !disabled && !iconRightProps?.disabled && styles.iconButtonInteractive,
                (disabled || iconRightProps?.disabled) && styles.iconButtonDisabled,
                iconRightProps?.containerClassName
            ),
        [iconRightProps?.onClick, disabled, iconRightProps?.disabled, iconRightProps?.containerClassName]
    );

    const errorTextClassNames = useMemo(
        () => cn(styles.helperText, styles.helperTextError, errorProps?.className),
        [errorProps?.className]
    );

    const helperTextClassNames = useMemo(
        () => cn(styles.helperText, styles.helperTextNormal, helperTextProps?.className),
        [helperTextProps?.className]
    );

    return {
        containerClassNames,
        labelOutsideClassNames,
        labelFloatingClassNames,
        inputClassNames,
        iconLeftContainerClassNames,
        iconRightContainerClassNames,
        errorTextClassNames,
        helperTextClassNames,
    };
};
